import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getGluceraAccessToken,
  getGluceraUserInfo,
  clearGluceraStorage,
  setGluceraAccessToken,
} from "../utils/storage";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import { jwtDecode } from "jwt-decode";

export const useGluceraAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Complete logout function - clear ALL sessions (Both Main App + Glucera)
  const logout = useCallback(() => {
    // 🧹 Clear ALL Bite Balance storage
    clearGluceraStorage();

    // 🧹 Clear main app storage (unified cleanup)
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("glucera_refresh_token");

    // 🧹 Clear profile and user data
    localStorage.removeItem("userProfile");
    localStorage.removeItem("profileImage");
    localStorage.removeItem("onboardingComplete");

    // 🧹 Clear Google OAuth and session storage
    sessionStorage.clear();

    // 🧹 Clear additional auth-related items
    localStorage.removeItem("tenantId");
    localStorage.removeItem("assessment_data");
    localStorage.removeItem("onboarding_completed");
    localStorage.removeItem("cached_meals");
    localStorage.removeItem("user_preferences");
    localStorage.removeItem("glucera_document_id");

    // 🧹 Clear any main app cached data
    localStorage.removeItem("preferencesData");

    // 🔄 Reset all authentication state
    setIsAuthenticated(false);
    setUser(null);
    setLoading(false);

    // Navigate to Bite Balance welcome page (correct path without double tenant)
    navigate("/glucera" + GluceraNavigationRoutes.ONBOARDING_WELCOME, {
      replace: true,
    });
  }, [navigate]);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    const token = getGluceraAccessToken();
    const userInfo = getGluceraUserInfo();

    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return false;
    }

    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      // If token will expire in next 5 minutes, try to refresh
      if (decoded.exp < currentTime + 300) {
        const refreshToken = localStorage.getItem("glucera_refresh_token");

        if (refreshToken) {
          try {
            // Try to refresh the token silently
            const response = await fetch(
              `${import.meta.env.VITE_BASE_URL}/auth/token/refresh/`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh: refreshToken }),
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.access) {
                setGluceraAccessToken(data.access);
                if (data.refresh) {
                  localStorage.setItem("glucera_refresh_token", data.refresh);
                }
                setIsAuthenticated(true);
                setUser(userInfo);
                setLoading(false);
                return true;
              }
            }
          } catch (error) {
            // Refresh failed, logout
            logout();
            return false;
          }
        }

        // No refresh token available
        logout();
        return false;
      }

      // Valid token
      setIsAuthenticated(true);
      setUser(userInfo);
      setLoading(false);
      return true;
    } catch (_error) {
      // Invalid token
      logout();
      return false;
    }
  }, [logout]);

  // Check auth on mount and token changes
  useEffect(() => {
    checkAuth();

    // Listen for storage changes (for multi-tab logout synchronization)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "glucera_access_token" && !e.newValue) {
        // Token was removed in another tab - complete logout
        // Clear remaining storage in this tab
        sessionStorage.clear();
        localStorage.removeItem("tenantId");
        localStorage.removeItem("assessment_data");
        localStorage.removeItem("onboarding_completed");

        setIsAuthenticated(false);
        setUser(null);
        navigate("/glucera" + GluceraNavigationRoutes.ONBOARDING_WELCOME, {
          replace: true,
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate, checkAuth]);

  return {
    isAuthenticated,
    user,
    loading,
    logout,
    checkAuth,
  };
};
